// Interfaces

export interface ExternalAccount {
	externalId?: string;
	code: string;
	name: string;
	type: string;
	class: string;
	status: string;
	enablePayments?: boolean;
}

export interface ChartOfAccounts {
	syncedAt?: string;
	accounts: ExternalAccount[];
}

export interface ExternalAccountOption {
	code: string;
	name?: string;
	label: string;
	// The currently mapped code is no longer an active account in the provider
	missing?: boolean;
}

export type AccountMappingField = 'salesAccount' | 'purchasesAccount' | 'bankAccount';

// Helpers

const accountFilters: Record<AccountMappingField, (account: ExternalAccount) => boolean> = {
	salesAccount: account => account.class === 'REVENUE',
	purchasesAccount: account => account.class === 'EXPENSE',
	bankAccount: account => account.type === 'BANK',
};

export function externalAccountLabel(account: Pick<ExternalAccount, 'code' | 'name'>): string {
	return account.name ? `${account.code} – ${account.name}` : account.code;
}

/**
 * Builds the selectable options for an account mapping field from the stored chart of accounts.
 * Only active accounts of the relevant kind are offered. If the current value isn't among them it
 * is still included so it stays visible: as a normal option when it's an active account of another
 * kind, or flagged as missing when it's archived or no longer exists.
 */
export function buildExternalAccountOptions(
	chart: ChartOfAccounts | undefined,
	field: AccountMappingField,
	currentCode?: string,
): ExternalAccountOption[] {
	const accounts = chart?.accounts ?? [];
	const options: ExternalAccountOption[] = accounts
		.filter(account => account.status === 'ACTIVE' && accountFilters[field](account))
		.map(account => ({ code: account.code, name: account.name, label: externalAccountLabel(account) }));

	if (currentCode && !options.some(option => option.code === currentCode)) {
		const account = accounts.find(account => account.code === currentCode);
		if (account?.status === 'ACTIVE') {
			options.unshift({ code: account.code, name: account.name, label: externalAccountLabel(account) });
		} else {
			options.unshift({
				code: currentCode,
				name: account?.name,
				label: `${externalAccountLabel({ code: currentCode, name: account?.name })} (not found in Xero)`,
				missing: true,
			});
		}
	}

	return options;
}
